/**
 * useSkerpaAutoGreet — Emits a proactive Skerpa message when the user navigates
 * to a new view while chat already has history. Silent on first visit (the
 * ProactiveWelcome card stack handles the empty state).
 *
 * Gates (all must pass before injecting a greeting):
 * 1. User opt-in: `skerpaPreferencesStore.autoGreet` is true.
 * 2. Dwell: user has stayed on the new view for ≥3 seconds (DWELL_MS).
 *    Prevents drive-by greets on quick tab switches.
 * 3. Cooldown: ≥60s since last greeting on this same view.
 * 4. State: chat has history and is not currently streaming.
 */

import { useEffect, useRef } from 'react'
import { useSkerpaContextStore } from '@/stores/skerpaContextStore'
import { useJourneyStore } from '@/stores/journeyStore'
import { useSkerpaPreferencesStore } from '@/stores/skerpaPreferencesStore'
import { getHermesChatState, injectAssistantMessage } from './useHermesChat'
import { buildAutoGreet } from '@/lib/skerpa/autoGreet'

const DWELL_MS = 3_000
const COOLDOWN_MS = 60_000

export function useSkerpaAutoGreet() {
  const activeView = useSkerpaContextStore(s => s.activeView)
  const pageContext = useSkerpaContextStore(s => s.pageContext)
  const canvasSectionState = useSkerpaContextStore(s => s.canvasSectionState)
  const getDomainScores = useJourneyStore(s => s.getDomainScores)
  const getCurrentPhase = useJourneyStore(s => s.getCurrentPhase)
  const autoGreetEnabled = useSkerpaPreferencesStore(s => s.autoGreet)

  const lastGreet = useRef<Map<string, number>>(new Map())
  const prevView = useRef<string>('')
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (!activeView || activeView === prevView.current) return
    prevView.current = activeView

    if (timer.current) clearTimeout(timer.current)

    if (!autoGreetEnabled) return

    timer.current = setTimeout(() => {
      const { messages, isStreaming } = getHermesChatState()
      // Empty-state: ProactiveWelcome cards already cover this view
      if (messages.length === 0 || isStreaming) return

      // Per-view cooldown — avoid re-greeting when user tab-hops back
      const last = lastGreet.current.get(activeView) || 0
      if (Date.now() - last < COOLDOWN_MS) return

      const phase = getCurrentPhase()
      const greet = buildAutoGreet({
        activeView,
        pageContext,
        canvasSectionState,
        domainScores: getDomainScores(),
        currentPhaseName: phase?.name || null,
        currentPhaseDomain: phase?.domain || null,
      })
      if (!greet) return

      if (injectAssistantMessage(greet)) {
        lastGreet.current.set(activeView, Date.now())
      }
    }, DWELL_MS)

    return () => {
      if (timer.current) clearTimeout(timer.current)
    }
  }, [activeView, pageContext, canvasSectionState, getDomainScores, getCurrentPhase, autoGreetEnabled])
}
